import { environment } from "../config/environment";
import {
  getFromBackendAPI,
  postToBackendAPI,
  patchToBackendAPI,
  deleteFromBackendAPI,
} from "./api.service";

/**
 * Task status constants
 */
export const TASK_STATUS = {
  TO_DO: "to_do",
  IN_PROGRESS: "in_progress",
  INCOMPLETE: "incomplete",
  COMPLETE: "complete",
} as const;

export type TaskStatus = (typeof TASK_STATUS)[keyof typeof TASK_STATUS];

/**
 * Task DTOs - matching backend API models
 */
export interface TaskDto {
  id: number;
  spreadsheetId: string;
  statusItemId?: number;
  name: string;
  outputType?: string;
  /** Legacy display string carried over from before task_definition_id existed. */
  taskType?: string;
  /** FK to the task definition that drove this task — null only for historical rows. */
  taskDefinitionId?: string;
  status: string;
  result?: string;
  agentMessages?: string;
  createdByEmail?: string;
  createdByName?: string;
  createdAt?: string;
  updatedAt?: string;
}

export interface CreateTaskRequest {
  spreadsheetId: string;
  statusItemId?: number;
  name: string;
  /** UUID of the task definition that drives this agent run. Required. */
  taskDefinitionId: string;
}

export interface CreateTasksRequest {
  spreadsheetId: string;
  items: CreateTaskItemRequest[];
}

export interface CreateTaskItemRequest {
  statusItemId?: number;
  name: string;
  /** UUID of the task definition that drives this agent run. Required. */
  taskDefinitionId: string;
}

export interface UpdateTaskStatusRequest {
  status: string;
}

export interface UpdateTaskResultRequest {
  result?: string;
  status?: string;
}

/**
 * Get all tasks for a given spreadsheet
 * @param spreadsheetId - The spreadsheet ID (GUID)
 * @returns Promise with array of tasks
 */
export async function getTasksBySpreadsheet(
  spreadsheetId: string
): Promise<TaskDto[]> {
  const endpoint = `${environment.api.baseUrl}${environment.api.endpoints.tasks}/spreadsheet/${spreadsheetId}`;
  return await getFromBackendAPI<TaskDto[]>(endpoint);
}

/**
 * Get a single task by ID
 * @param taskId - The task ID
 * @returns Promise with the task
 */
export async function getTask(taskId: number): Promise<TaskDto> {
  const endpoint = `${environment.api.baseUrl}${environment.api.endpoints.tasks}/${taskId}`;
  return await getFromBackendAPI<TaskDto>(endpoint);
}

/**
 * Create a new task
 * @param task - The task data to create
 * @returns Promise with the created task
 */
export async function createTask(task: CreateTaskRequest): Promise<TaskDto> {
  const endpoint = `${environment.api.baseUrl}${environment.api.endpoints.tasks}`;
  return await postToBackendAPI<TaskDto>(endpoint, task);
}

/**
 * Create multiple tasks at once
 * @param request - The bulk create request with spreadsheetId and items
 * @returns Promise with array of created tasks
 */
export async function createTasks(
  request: CreateTasksRequest
): Promise<TaskDto[]> {
  const endpoint = `${environment.api.baseUrl}${environment.api.endpoints.tasks}/bulk`;
  return await postToBackendAPI<TaskDto[]>(endpoint, request);
}

/**
 * Update the status of a task
 * @param taskId - The task ID
 * @param status - The new status
 * @returns Promise with the updated task
 */
export async function updateTaskStatus(
  taskId: number,
  status: string
): Promise<TaskDto> {
  const endpoint = `${environment.api.baseUrl}${environment.api.endpoints.tasks}/${taskId}/status`;
  return await patchToBackendAPI<TaskDto>(endpoint, { status });
}

/**
 * Update the result of a task
 * @param taskId - The task ID
 * @param result - The result data (JSON string)
 * @param status - Optional new status
 * @returns Promise with the updated task
 */
export async function updateTaskResult(
  taskId: number,
  result?: string,
  status?: string
): Promise<TaskDto> {
  const endpoint = `${environment.api.baseUrl}${environment.api.endpoints.tasks}/${taskId}/result`;
  return await patchToBackendAPI<TaskDto>(endpoint, { result, status });
}

/**
 * Delete a task
 * @param taskId - The task ID
 * @returns Promise that resolves when deleted
 */
export async function deleteTask(taskId: number): Promise<void> {
  const endpoint = `${environment.api.baseUrl}${environment.api.endpoints.tasks}/${taskId}`;
  await deleteFromBackendAPI(endpoint);
}
